import { hasPermission } from "@avela/avela-authorization-sdk";
import { useMemo } from "react";
import { useAuthorizationScoped } from "src/components/Providers/AuthorizationProvider";
import { useEnrollmentPeriod } from "src/components/Providers/EnrollmentPeriodProvider";
import { useTags } from "src/scenes/orgAdmin/tags/useTags";
import * as GQL from "src/types/graphql";
import { useSchoolAdmin } from "./useSchoolAdmin";

export function useDefaultColumnsProps({
  formTemplate,
}: {
  formTemplate: {
    sections: GQL.GetFormTemplatesByForLeftNavigation_form_template_sections[];
    form_verifications: GQL.GetFormTemplatesByForLeftNavigation_form_template_form_verifications[];
  } | null;
}) {
  const { isSchoolAdmin } = useSchoolAdmin();

  const authorizationMap = useAuthorizationScoped("permissions");
  const hasOfferReadPermission = useMemo(() => {
    if (!authorizationMap.hasData()) {
      return false;
    }
    return hasPermission({
      action: {
        type: "Perform::Action",
        id: "with_permission",
      },
      resource: {
        type: "Permission",
        id: "offer:read",
      },
      authorizationMap: authorizationMap.data,
    });
  }, [authorizationMap]);

  const { selectedEnrollmentPeriod } = useEnrollmentPeriod();
  const { tagGroups } = useTags({
    enrollmentPeriodId: selectedEnrollmentPeriod?.id ?? "",
    tagGroupId: null,
    isEditing: false,
  });

  const rankingEnabled = useMemo(
    () =>
      formTemplate?.sections.find((section) => section.schools_ranking_section)
        ?.schools_ranking_section?.ranking_enabled ?? false,
    [formTemplate?.sections]
  );

  const hasVerifications = useMemo(
    () => (formTemplate?.form_verifications?.length ?? 0) > 0,
    [formTemplate?.form_verifications]
  );

  const defaultColumnsProps = useMemo(() => {
    return {
      isSchoolAdmin,
      hasOfferReadPermission,
      tagGroups,
      rankingEnabled,
      hasVerifications,
    };
  }, [
    isSchoolAdmin,
    hasOfferReadPermission,
    tagGroups,
    rankingEnabled,
    hasVerifications,
  ]);

  return defaultColumnsProps;
}
